import React, { useRef, useEffect, useCallback, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useTranslation } from "react-i18next";
import PostCard from "./PostCard";
import { usePostsAdmin } from "@/hooks/admin/usePostsAdmin";
import { deletePost } from "../../lib/community";

export default function PostsTab({
  search,
  isDeleted,
}: {
  search?: string;
  isDeleted: boolean;
}) {
  const { t } = useTranslation();
  const {
    queryClient,
    posts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = usePostsAdmin(search, isDeleted);

  const observerRef = useRef<HTMLDivElement | null>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    if (!observerRef.current) return;
    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: "400px 0px",
      threshold: 0.1,
    });
    observer.observe(observerRef.current);

    return () => {
      if (observerRef.current) observer.unobserve(observerRef.current);
    };
  }, [handleObserver, posts.length, hasNextPage, isFetchingNextPage]);

  const handleDelete = async (postId: string) => {
    await deletePost(postId);

    queryClient.invalidateQueries({ queryKey: ["posts"] });
    queryClient.invalidateQueries({ queryKey: ["admin-posts"] });
    refetch();
  };

  return (
    <div className="mt-4">
      <div className="mt-4 flex flex-col gap-4">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-48 bg-gray-200 rounded-lg" />
          ))
        ) : posts.length === 0 ? (
          <div className="text-center text-gray-400">
            {t("community.notFound")}
          </div>
        ) : (
          posts.map((post) => (
            <PostCard key={post.id} post={post} onDelete={handleDelete} />
          ))
        )}
      </div>
      <div ref={observerRef} id="infinite-scroll-trigger" className="h-4" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-4">
          <Spinner className="h-8 w-8" />
        </div>
      )}
    </div>
  );
}
